/* eslint-disable react-hooks/exhaustive-deps */
import React, {
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
} from "react";
import { Link, useSearchParams } from "react-router-dom";
import style from "../styles/shop.module.scss";
import Banner from "../components/shared/Banner";
import ProductCard from "../components/shared/ProductCard";
import ReactPaginate from "react-paginate";
import Loader from "../components/shared/Loader";
import { GlobalContext } from "../context/GlobalContext";
import withHelmet from "../seo/withHelmet";
import { shopHelmetProps } from "../seo/helmetProps";
import { debounce } from "lodash";
import { categoryLink } from "../utils/categoryLink";

const Shop = () => {
  const {
    getProducts,
    products,
    loading,
    pagination,
    getCategories,
    categories,
  } = useContext(GlobalContext);

  const [searchParams, setSearchParams] = useSearchParams();

  // Helper function to find category ID by slug
  const findCategoryIdBySlug = useCallback(
    (categorySlug) => {
      if (!categorySlug || !categories.length) return null;
      for (const category of categories) {
        const found = category.subcategories.find(
          (sub) => sub.slug === categorySlug
        );
        if (found) return found.documentId;
      }
      return null;
    },
    [categories]
  );

  // Helper function to find category slug by ID
  const findCategorySlugById = useCallback(
    (categoryId) => {
      if (!categoryId || !categories.length) return null;
      for (const category of categories) {
        const found = category.subcategories.find(
          (sub) => sub.documentId === categoryId
        );
        if (found) return found.slug;
      }
      return null;
    },
    [categories]
  );

  // Initialize state from URL params
  const [page, setPage] = useState(() => {
    const urlPage = searchParams.get("page");
    return urlPage ? parseInt(urlPage) : 1;
  });
  const [totalPages, setTotalPages] = useState(1);
  const initialCategoryParts = (searchParams.get("category") || "")
    .split(",")
    .map((slug) => slug.trim())
    .filter(Boolean);
  const [selectedSubctegory, setSelectedSubctegory] = useState(() =>
    initialCategoryParts.length === 1 ? initialCategoryParts[0] : null
  );
  const [categoryGroup, setCategoryGroup] = useState(() =>
    initialCategoryParts.length > 1 ? initialCategoryParts : null
  );
  const [otherFilter, setOtherFilter] = useState(() => {
    return searchParams.get("filter") || null;
  });
  const [search, setSearch] = useState(() => {
    return searchParams.get("search") || "";
  });
  const [searchInput, setSearchInput] = useState(() => {
    return searchParams.get("search") || "";
  });
  const [hasConvertedFromUrl, setHasConvertedFromUrl] = useState(false);
  const isFirstRender = useRef(true);
  const appliedParams = useRef(searchParams.toString());

  useEffect(() => {
    const current = searchParams.toString();
    if (current === appliedParams.current) return;
    appliedParams.current = current;

    const parts = (searchParams.get("category") || "")
      .split(",")
      .map((slug) => slug.trim())
      .filter(Boolean);
    const urlPage = parseInt(searchParams.get("page") || "1", 10);
    setPage(Number.isNaN(urlPage) ? 1 : urlPage);
    setOtherFilter(searchParams.get("filter") || null);
    const urlSearch = searchParams.get("search") || "";
    setSearch(urlSearch);
    setSearchInput(urlSearch);

    if (parts.length > 1) {
      setCategoryGroup(parts);
      setSelectedSubctegory(null);
      setHasConvertedFromUrl(true);
    } else if (parts.length === 1) {
      setCategoryGroup(null);
      setSelectedSubctegory(parts[0]);
      setHasConvertedFromUrl(false);
    } else {
      setCategoryGroup(null);
      setSelectedSubctegory(null);
      setHasConvertedFromUrl(true);
    }
  }, [searchParams]);
  const categoryGroupLabel = (() => {
    if (!categoryGroup || categories.length === 0) return "";
    const selected = new Set(categoryGroup);
    const parent = categories.find((category) => {
      const slugs = (category.subcategories || [])
        .map((sub) => sub.slug)
        .filter(Boolean);
      return (
        slugs.length === categoryGroup.length &&
        slugs.every((slug) => selected.has(slug))
      );
    });
    return parent?.name || "Kategoria";
  })();

  const activeSubSlug = (() => {
    if (categoryGroup || !selectedSubctegory) return null;
    return (
      findCategorySlugById(selectedSubctegory) ||
      (categories.some((category) =>
        (category.subcategories || []).some(
          (sub) => sub.slug === selectedSubctegory
        )
      )
        ? selectedSubctegory
        : null)
    );
  })();

  const scopeParent = categories.find((category) => {
    const slugs = (category.subcategories || [])
      .map((sub) => sub.slug)
      .filter(Boolean);
    if (categoryGroup?.length) {
      const selected = new Set(categoryGroup);
      return (
        slugs.length === categoryGroup.length &&
        slugs.every((slug) => selected.has(slug))
      );
    }
    return activeSubSlug ? slugs.includes(activeSubSlug) : false;
  });

  // Update URL params when filters change
  useEffect(() => {
    if (categories.length === 0) return;

    const params = new URLSearchParams();

    if (page > 1) params.set("page", page.toString());
    if (categoryGroup?.length > 1) {
      params.set("category", categoryGroup.join(","));
    } else if (
      selectedSubctegory &&
      selectedSubctegory !== "" &&
      selectedSubctegory !== "Kategoria"
    ) {
      const categorySlug = findCategorySlugById(selectedSubctegory);
      if (categorySlug) {
        params.set("category", categorySlug);
      }
    }
    if (otherFilter) params.set("filter", otherFilter);
    if (search) params.set("search", search);

    appliedParams.current = params.toString();
    setSearchParams(params, { replace: true });
  }, [
    page,
    selectedSubctegory,
    categoryGroup,
    otherFilter,
    search,
    setSearchParams,
    findCategorySlugById,
    categories.length,
  ]);

  useEffect(() => {
    getCategories();
  }, []);

  // Convert URL category slug to ID when categories load
  useEffect(() => {
    if (categories.length > 0 && selectedSubctegory && !hasConvertedFromUrl) {
      const categoryId = findCategoryIdBySlug(selectedSubctegory);
      if (categoryId && categoryId !== selectedSubctegory) {
        setSelectedSubctegory(categoryId);
        setHasConvertedFromUrl(true);
      } else if (!categoryId && categories.length > 0) {
        const isValidId = categories.some((cat) =>
          cat.subcategories.some((sub) => sub.documentId === selectedSubctegory)
        );
        if (isValidId) {
          setHasConvertedFromUrl(true);
        } else {
          setSelectedSubctegory(null);
          setHasConvertedFromUrl(true);
        }
      }
    }
  }, [
    categories,
    findCategoryIdBySlug,
    selectedSubctegory,
    hasConvertedFromUrl,
  ]);

  useEffect(() => {
    if (pagination) {
      setTotalPages(pagination.pageCount);
    }
  }, [pagination]);

  const handleSearch = useCallback(
    debounce((value) => {
      setSearch(value);
      setPage(1);
    }, 500),
    []
  );

  const handleSearchInputChange = (e) => {
    const value = e.target.value;
    setSearchInput(value);
    handleSearch(value);
  };
  useEffect(() => {
    // Skip on first render
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    // Don't call API if we have a category slug but it hasn't been converted yet
    if (selectedSubctegory && !hasConvertedFromUrl && categories.length > 0) {
      return;
    }

    const groupIds = categoryGroup
      ? categoryGroup
          .map((slug) => findCategoryIdBySlug(slug))
          .filter(Boolean)
      : null;

    if (
      categoryGroup &&
      (categories.length === 0 || groupIds.length !== categoryGroup.length)
    ) {
      return;
    }

    getProducts(
      page,
      8,
      categoryGroup
        ? groupIds
        : selectedSubctegory === "Kategoria" ||
          selectedSubctegory === "Të Gjitha"
        ? null
        : selectedSubctegory,
      otherFilter === "price:asc" ||
        otherFilter === "price:desc" ||
        otherFilter === "createdAt:desc"
        ? otherFilter
        : null,
      otherFilter === "discount" ? true : null,
      otherFilter === "top" ? true : null,
      search
    );
  }, [
    page,
    selectedSubctegory,
    categoryGroup,
    otherFilter,
    search,
    hasConvertedFromUrl,
    categories.length,
    findCategoryIdBySlug,
  ]);

  return (
    <div className={style.wrapper}>
      <Banner
        title={
          activeSubSlug
            ? scopeParent?.subcategories?.find(
                (sub) => sub.slug === activeSubSlug
              )?.name || "Shop"
            : scopeParent?.name || "Shop"
        }
        current={"Shop"}
        compact
        from={[{ name: "Ballina", link: "/" }]}
      />

      {scopeParent ? (
        <div className={style.subnav}>
          <Link
            to={categoryLink(scopeParent)}
            className={!activeSubSlug ? style.subActive : undefined}
          >
            Të gjitha në {scopeParent.name}
          </Link>
          {(scopeParent.subcategories || []).map((sub) => (
            <Link
              key={sub.id || sub.slug}
              to={`/shop?category=${sub.slug}`}
              className={activeSubSlug === sub.slug ? style.subActive : undefined}
            >
              {sub.name}
            </Link>
          ))}
        </div>
      ) : null}

      <div className={style.filters}>
        <input
          type="text"
          placeholder="Kërko..."
          value={searchInput}
          onChange={handleSearchInputChange}
        />
        {categories.length === 0 || categories === undefined ? (
          <></>
        ) : (
          <div className={style.selects}>
            <select
              value={categoryGroup ? "__group" : selectedSubctegory || ""}
              onChange={(e) => {
                setPage(1);
                setCategoryGroup(null);
                setSelectedSubctegory(e.target.value || null);
                setHasConvertedFromUrl(true);
              }}
            >
              <option value="Kategoria" disabled>
                Kategoria
              </option>
              {categoryGroup ? (
                <option value="__group">{categoryGroupLabel}</option>
              ) : null}
              <option value="">Të Gjitha</option>
              {categories.map((category) => (
                <React.Fragment key={category.id}>
                  <option disabled>{category.name}</option>

                  {category.subcategories.map((sub) => (
                    <option key={sub.id} value={sub.documentId}>
                      {sub.name}
                    </option>
                  ))}
                </React.Fragment>
              ))}
            </select>
            <select
              value={otherFilter || ""}
              onChange={(e) => {
                setPage(1);
                setOtherFilter(e.target.value || null);
              }}
            >
              <option value="">Sipas Relevancës</option>
              <option value={"price:asc"}>Çmimi më i ulët</option>
              <option value={"price:desc"}>Çmimi më i lartë</option>
              <option value={"discount"}>Me Zbritje</option>
              <option value={"createdAt:desc"}>Më të rejat</option>
              <option value={"top"}>Më të shiturat</option>
            </select>
          </div>
        )}
      </div>

      {loading || products === undefined || pagination === undefined ? (
        <Loader />
      ) : (
        <>
          {products.length === 0 ? (
            <h1>Nuk ka produkte</h1>
          ) : (
            <div className={style.products}>
              {products?.map((product) => {
                return <ProductCard product={product} key={product?.id} />;
              })}
            </div>
          )}
        </>
      )}
      <div className={style.paginationWrapper}>
        <ReactPaginate
          containerClassName={style.pagination}
          pageClassName={style.pageItem}
          activeClassName={style.active}
          onPageChange={(event) => {
            setPage(event.selected + 1);
            window.scrollTo({
              top: 0,
              behavior: "smooth",
            });
          }}
          pageCount={totalPages}
          forcePage={page - 1}
          breakLabel="..."
          previousLabel={false}
          nextLabel={false}
        />
      </div>
    </div>
  );
};

export default withHelmet(Shop, shopHelmetProps);
